import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { orderManager } from "../../data/managers/OrderManager";
import { CashModel } from "../../data/models/cash/CashModel";
import { Customer } from "../../data/models/customers/CustomerModel";
import { OrderModel } from "../../data/models/order/OrderModel";
import {
  TypeDiscountEnum,
  TypePaymentMethodEnum,
} from "../../data/models/order/PaymentModel";
import { Product } from "../../data/models/product/ProductModel";
import { useSnackbar } from "../../data/providers/SnackbarProvider";
import { useCash } from "./useCash";
import { useCustomers } from "./useCustomers";
import { useProducts } from "./useProducts";

export interface OrderItem {
  id?: string;
  product: Product;
  quantity: number;
  comments: string;
  createdAt?: Date;
}

export const useModuleOrder = () => {
  const { products, categories, refreshProducts } = useProducts({
    requestHard: false,
  });
  const { customers, refreshCustomers } = useCustomers();
  const { cashes } = useCash();
  const [cashesOpened, setCashesOpened] = useState<CashModel[] | null>(null);

  const [items, setItems] = useState<OrderItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );

  const [discountType, setDiscountType] = useState<TypeDiscountEnum>(
    TypeDiscountEnum.FLAT
  );
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [openDiscount, setOpenDiscount] = useState(false);
  const [payment, setPayment] = useState<TypePaymentMethodEnum | null>(null);
  const { showSnackbar, showError } = useSnackbar();

  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get("moduleId");

  const [orderData, setOrderData] = useState<OrderModel | null>(null);
  const [loadingOrder, setLoadingModule] = useState(true);
  const [errorOrder, setErrorModule] = useState<string | null>(null);
  const [showButtonPreparation, setShowButtonPreparation] =
    useState<boolean>(true);

  const smoothLoading = (fn: () => Promise<void>) => {
    setLoadingModule(true);
    const delay = new Promise((res) => setTimeout(res, 200));

    return Promise.all([fn(), delay])
      .catch(() => {})
      .finally(() => setLoadingModule(false));
  };

  useEffect(() => {
    if (!moduleId) return;
    refreshOrder();
  }, []);

  useEffect(() => {
    if (!moduleId) return;
    const selected =
      customers.find((c) => c.id === orderData?.customerId) ?? null;
    setSelectedCustomer(selected);
  }, [customers]);

  useEffect(() => {
    if (!moduleId) return;
    const syncedItems: OrderItem[] = [];
    for (const i of orderData?.items ?? []) {
      const p = products.find((prod) => prod.id === i.productId);
      if (!p) continue;
      syncedItems.push({
        id: i.id,
        product: p,
        quantity: i.quantity,
        comments: i.comments,
        createdAt: i.createdAt,
      });
    }
    syncedItems.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : null;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : null;
      if (dateA === null && dateB === null) return 0;
      if (dateA === null) return 1;
      if (dateB === null) return -1;
      return dateA - dateB;
    });
    setItems(syncedItems);
  }, [products]);

  useEffect(() => {
    if (!moduleId) return;
    setCashesOpened(cashes.filter((c) => c.session));
  }, [cashes]);

  const refreshOrder = async () => {
    try {
      setLoadingModule(true);
      setErrorModule(null);

      const r = await orderManager.getOrCreateManager(moduleId!);
      const data = r?.data;
      if (!data) throw new Error("No se recibió información del servidor");
      setOrderData(data);

      setShowButtonPreparation(true);
      refreshCustomers();
      refreshProducts();
    } catch (err: any) {
      setErrorModule(err?.message ?? "Error desconocido al cargar la orden");
    } finally {
      setLoadingModule(false);
    }
  };

  const assignCustomer = async ({
    orderId,
    customerId,
  }: {
    orderId: string;
    customerId: string;
  }) => {
    try {
      setLoadingModule(true);
      const r = await orderManager.assignCustomer({ orderId, customerId });
      if (!r) throw new Error("new errors");
      const _customerSelected =
        customers.find((e) => e.id === customerId) ?? null;
      setSelectedCustomer(_customerSelected);
    } catch (err: any) {
    } finally {
      setLoadingModule(false);
    }
  };

  const addProduct = async (
    product: Product,
    quantity: number,
    comments: string
  ) => {
    setItems((prev) => {
      return [...prev, { product, quantity, comments }];
    });
    await smoothLoading(async () => {
      try {
        await orderManager.addItem({
          orderId: orderData?.id!,
          productId: product.id!,
          quantity: quantity,
          moduleId: moduleId!,
          comments,
        });
        sendOrderToPreparation();
        await refreshOrder();
        showSnackbar("Producto agregado");
      } catch (err) {
        showError("No se pudo agregar el producto");
        await refreshOrder();
      }
    });
  };

  // const updateQuantity = async (id: string, qty: number) => {
  //   if (qty <= 0) return removeProduct(id);
  //   setItems((prev) =>
  //     prev.map((i) => (i.product.id === id ? { ...i, quantity: qty } : i))
  //   );
  //   await smoothLoading(async () => {
  //     try {
  //       // await orderManager.addItem({
  //       //   orderId: orderData?.id!,
  //       //   productId: id,
  //       //   quantity: qty,
  //       //   moduleId: moduleId!,
  //       // });
  //       sendOrderToPreparation();
  //       await refreshOrder();
  //       showSnackbar("Cantidad actualizada");
  //     } catch {
  //       showError("No se pudo actualizar la cantidad");
  //       await refreshOrder();
  //     }
  //   });
  // };

  const removeProduct = async (id: string, notes: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    await smoothLoading(async () => {
      try {
        await orderManager.deleteItem({
          itemId: id,
          notes,
        });
        sendOrderToPreparation();
        await refreshOrder();
        showSnackbar("Producto eliminado");
      } catch {
        showError("No se pudo eliminar el producto");
        await refreshOrder();
      }
    });
  };

  const sendToPreparation = async () => {
    try {
      setLoadingModule(true);
      const r = await orderManager.sendToPreparation({
        orderId: orderData?.id!,
      });
      if (!r) throw new Error("new errors");
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoadingModule(false);
      await refreshOrder();
    }
  };

  const sendOrderToPreparation = async () => {
    try {
      if (!orderData?.hasStation) return;
      const r = await orderManager.sendToPreparation({
        orderId: orderData?.id!,
      });
    } catch (err: any) {
      console.log(err);
    }
  };

  const handlePayOrder = async ({
    cashId,
    paymentMethod,
    discountType,
    discountValue,
  }: {
    cashId: string;
    paymentMethod: TypePaymentMethodEnum;
    discountType: TypeDiscountEnum;
    discountValue: number;
  }) => {
    try {
      setLoadingModule(true);
      const r = await orderManager.payOrder({
        orderId: orderData?.id!,
        cashId,
        paymentMethod,
        moduleId: moduleId!,
        discountType,
        discountValue,
      });
      if (!r) throw new Error("new errors");
      setOrderData(r);
      return r;
    } catch (err: any) {
      console.log(err);
      return undefined;
    } finally {
      setLoadingModule(false);
    }
  };

  const clearOrder = () => {
    setItems([]);
    setSelectedCustomer(null);
    setDiscountValue(0);
  };

  const subtotal = useMemo(() => {
    return items.reduce((acc, i) => acc + i.product.salePrice * i.quantity, 0);
  }, [items]);

  const total = useMemo(() => {
    if (discountType === TypeDiscountEnum.FLAT) {
      return Math.max(0, subtotal - discountValue);
    }
    if (discountType === TypeDiscountEnum.PERCENT) {
      return Math.max(0, subtotal - (subtotal * discountValue) / 100);
    }
    return subtotal;
  }, [subtotal, discountType, discountValue]);

  return {
    items,
    addProduct,
    // updateQuantity,
    removeProduct,
    clearOrder,
    subtotal,
    total,
    selectedCustomer,
    setSelectedCustomer,
    discountType,
    setDiscountType,
    discountValue,
    setDiscountValue,
    openDiscount,
    setOpenDiscount,
    payment,
    setPayment,
    moduleId,
    orderData,
    loadingOrder,
    errorOrder,
    products,
    categories,
    customers,
    assignCustomer,
    showButtonPreparation,
    sendToPreparation,
    cashesOpened,
    handlePayOrder,
    showError,
  };
};
